import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Rnd } from 'react-rnd';
import {
    PresentationTemplate, PresentationSlide, PresentationWidget,
    PresentationWidgetType, ProjectInfo, MasterReportTemplate,
} from '../../types';
import WidgetRenderer from './WidgetRenderer';
import WidgetCatalog from './WidgetCatalog';
import {
    saveTemplate, fetchTemplates, deleteTemplate,
    saveMasterTemplate, fetchMasterTemplates, deleteMasterTemplate,
} from '../../services/presentationService';

import { WeeklyPlanDataProps } from './WeeklyPlanTableWidget';

interface PresentationEditorProps {
    projectInfo: ProjectInfo;
    onClose: () => void;
    currentUserName?: string;
    weeklyPlanData?: WeeklyPlanDataProps;
    initialTemplate?: PresentationTemplate;
    isMasterMode?: boolean;
    onSaveMaster?: (template: PresentationTemplate) => void;
}

const genId = () => Math.random().toString(36).substring(2, 10);

const createEmptySlide = (order: number): PresentationSlide => ({
    id: genId(),
    order,
    widgets: [],
    background: '#ffffff',
});

const createDefaultTemplate = (projectId: string, userName?: string): PresentationTemplate => ({
    id: '',
    projectId,
    name: 'Nueva Presentación',
    slides: [createEmptySlide(0)],
    createdBy: userName,
});

export const SLIDE_THEMES = [
    { id: 'white', name: 'Blanco Clásico', bg: '#ffffff', desc: 'Fondo limpio para reportes tradicionales', isDark: false },
    { id: 'dark-slate', name: 'Oscuro Ejecutivo', bg: '#0f172a', desc: 'Alto contraste, visualización moderna', isDark: true },
    { id: 'corp-blue', name: 'Azul LD Corporativo', bg: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)', desc: 'Identidad constructora y comités', isDark: true },
    { id: 'emerald', name: 'Esmeralda Sostenible', bg: 'linear-gradient(135deg, #064e3b 0%, #022c22 100%)', desc: 'Ecológico, seguridad y medio ambiente', isDark: true },
    { id: 'tech-indigo', name: 'Índigo Innovación', bg: 'linear-gradient(135deg, #312e81 0%, #1e1b4b 100%)', desc: 'Tecnología BIM, analítica y control', isDark: true },
    { id: 'technical-gray', name: 'Gris Técnico Obra', bg: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', desc: 'Sutil y profesional para planos y tablas', isDark: false },
    { id: 'warm-sand', name: 'Arena Cálida', bg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', desc: 'Tonos constructivos, hormigón y tierra', isDark: false },
    { id: 'clean-sky', name: 'Cielo Limpio', bg: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', desc: 'Ligero y fresco con matices azulados', isDark: false },
    { id: 'graphite', name: 'Grafito Minimalista', bg: 'linear-gradient(135deg, #18181b 0%, #27272a 100%)', desc: 'Elegancia sobria para comités de obra', isDark: true },
    { id: 'slate-modern', name: 'Pizarra Moderna', bg: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)', desc: 'Equilibrio contemporáneo de contraste medio', isDark: true },
];

/**
 * Main WYSIWYG Presentation Editor with 16:9 canvas, slide management,
 * drag-and-drop/resize widgets via react-rnd, and Supabase persistence.
 */
const PresentationEditor: React.FC<PresentationEditorProps> = ({
    projectInfo,
    onClose,
    currentUserName,
    weeklyPlanData,
    initialTemplate,
    isMasterMode,
    onSaveMaster,
}) => {
    // --- State ---
    const [templates, setTemplates] = useState<PresentationTemplate[]>([]);
    const [activeTemplate, setActiveTemplate] = useState<PresentationTemplate>(() => {
        if (initialTemplate) return initialTemplate;
        return createDefaultTemplate(isMasterMode ? 'global' : (projectInfo?.id || ''), currentUserName);
    });
    const [activeSlideIndex, setActiveSlideIndex] = useState(0);
    const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
    const [showCatalog, setShowCatalog] = useState(false);
    const [showTemplateList, setShowTemplateList] = useState(false);
    const [showThemePicker, setShowThemePicker] = useState(false);
    const [multiPagePrompt, setMultiPagePrompt] = useState<{
        template: MasterReportTemplate;
        resolvedPages: { id: string; name: string }[];
    } | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
    const [isEditingName, setIsEditingName] = useState(false);
    const [editNameValue, setEditNameValue] = useState('');

    const canvasRef = useRef<HTMLDivElement>(null);

    const activeSlide = activeTemplate.slides[activeSlideIndex] || activeTemplate.slides[0];

    // --- Split a large weekly widget into multiple consecutive slides ---
    const splitWidgetIntoSlides = useCallback((widgetId: string) => {
        const currentSlide = activeTemplate.slides[activeSlideIndex];
        if (!currentSlide) return;
        const targetWidget = currentSlide.widgets.find(w => w.id === widgetId);
        if (!targetWidget) return;

        // Calculate total allocations for target plan
        const sortedPlans = weeklyPlanData?.weeklyPlans && weeklyPlanData.weeklyPlans.length > 0
            ? [...weeklyPlanData.weeklyPlans].sort((a, b) => a.id - b.id)
            : [];

        let targetPlan = null;
        if (targetWidget.type === 'weekly_current') {
            if (weeklyPlanData?.activePlanId) {
                targetPlan = sortedPlans.find(p => p.id === weeklyPlanData.activePlanId);
            }
            if (!targetPlan) {
                targetPlan = sortedPlans.find(p => p.status !== 'Cerrada') || sortedPlans[sortedPlans.length - 1];
            }
        } else {
            if (weeklyPlanData?.activePlanId) {
                const cIdx = sortedPlans.findIndex(p => p.id === weeklyPlanData.activePlanId);
                if (cIdx > 0) targetPlan = sortedPlans[cIdx - 1];
            }
            if (!targetPlan) {
                targetPlan = sortedPlans.length >= 2 ? sortedPlans[sortedPlans.length - 2] : sortedPlans[0];
            }
        }

        if (!targetPlan || !targetPlan.allocations || targetPlan.allocations.length === 0) return;

        const filteredCount = targetPlan.allocations.filter(alloc => {
            if (targetWidget.type === 'weekly_prev_executed') return alloc.executed === true;
            if (targetWidget.type === 'weekly_prev_not_executed') return alloc.executed === false;
            return true;
        }).length;

        const pageSize = targetWidget.config?.pageSize || 12;
        const totalPages = Math.ceil(filteredCount / pageSize);
        if (totalPages <= 1) {
            alert('Todas las tareas caben en una sola diapositiva.');
            return;
        }

        // Update the current slide's widget to page 0
        const updatedCurrentWidget: PresentationWidget = {
            ...targetWidget,
            config: { ...targetWidget.config, pageIndex: 0, pageSize }
        };

        // Create new slides for pages 1 .. totalPages - 1
        const newSlidesToInsert: PresentationSlide[] = [];
        for (let p = 1; p < totalPages; p++) {
            const continuationWidget: PresentationWidget = {
                ...JSON.parse(JSON.stringify(targetWidget)),
                id: genId(),
                config: { ...targetWidget.config, pageIndex: p, pageSize }
            };
            newSlidesToInsert.push({
                id: genId(),
                order: 0, // will be recalculated
                background: currentSlide.background || '#ffffff',
                widgets: [continuationWidget]
            });
        }

        // Insert new slides directly after current slide
        setActiveTemplate(prev => {
            const before = prev.slides.slice(0, activeSlideIndex);
            const currentWithUpdatedWidget: PresentationSlide = {
                ...currentSlide,
                widgets: currentSlide.widgets.map(w => w.id === widgetId ? updatedCurrentWidget : w)
            };
            const after = prev.slides.slice(activeSlideIndex + 1);
            const combined = [...before, currentWithUpdatedWidget, ...newSlidesToInsert, ...after].map((s, idx) => ({
                ...s,
                order: idx
            }));
            return { ...prev, slides: combined };
        });

        alert(`Se han generado ${totalPages - 1} diapositiva(s) adicional(es) para mostrar las tareas restantes.`);
    }, [activeSlideIndex, activeTemplate.slides, weeklyPlanData]);

    // --- Load templates on mount ---
    useEffect(() => {
        if (isMasterMode) {
            fetchMasterTemplates().then(setTemplates).catch(console.error);
        } else if (projectInfo.id) {
            fetchTemplates(projectInfo.id).then(setTemplates).catch(console.error);
        }
    }, [isMasterMode, projectInfo.id]);

    // --- Slide Management ---
    const updateSlides = useCallback((updater: (slides: PresentationSlide[]) => PresentationSlide[]) => {
        setActiveTemplate(prev => ({ ...prev, slides: updater(prev.slides) }));
    }, []);

    const addSlide = useCallback(() => {
        updateSlides(slides => {
            const newSlide = createEmptySlide(slides.length);
            return [...slides, newSlide];
        });
        setActiveSlideIndex(activeTemplate.slides.length);
    }, [updateSlides, activeTemplate.slides.length]);

    const duplicateSlide = useCallback((index: number) => {
        updateSlides(slides => {
            const source = slides[index];
            const dup: PresentationSlide = {
                ...JSON.parse(JSON.stringify(source)),
                id: genId(),
                order: slides.length,
            };
            // Give each widget a new ID
            dup.widgets = dup.widgets.map((w: PresentationWidget) => ({ ...w, id: genId() }));
            return [...slides, dup];
        });
        setActiveSlideIndex(activeTemplate.slides.length);
    }, [updateSlides, activeTemplate.slides.length]);

    const deleteSlide = useCallback((index: number) => {
        if (activeTemplate.slides.length <= 1) {
            alert('La presentación debe contener al menos una diapositiva.');
            return;
        }
        updateSlides(slides => {
            const filtered = slides.filter((_, i) => i !== index);
            return filtered.map((s, i) => ({ ...s, order: i }));
        });
        if (activeSlideIndex >= activeTemplate.slides.length - 1) {
            setActiveSlideIndex(Math.max(0, activeTemplate.slides.length - 2));
        }
        setSelectedWidgetId(null);
    }, [activeTemplate.slides.length, activeSlideIndex, updateSlides]);

    const reorderSlide = useCallback((fromIndex: number, toIndex: number) => {
        if (toIndex < 0 || toIndex >= activeTemplate.slides.length) return;
        updateSlides(slides => {
            const copy = [...slides];
            const [moved] = copy.splice(fromIndex, 1);
            copy.splice(toIndex, 0, moved);
            return copy.map((s, i) => ({ ...s, order: i }));
        });
        setActiveSlideIndex(toIndex);
    }, [activeTemplate.slides.length, updateSlides]);

    const moveSlide = useCallback((index: number, direction: 'up' | 'down') => {
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        reorderSlide(index, targetIndex);
    }, [reorderSlide]);

    // --- Widget Management ---
    const addWidget = useCallback((type: PresentationWidgetType, defaultWidth: number, defaultHeight: number) => {
        const newWidget: PresentationWidget = {
            id: genId(),
            type,
            x: 10,
            y: 10,
            width: defaultWidth,
            height: defaultHeight,
            config: {},
        };
        updateSlides(slides =>
            slides.map((s, i) =>
                i === activeSlideIndex
                    ? { ...s, widgets: [...s.widgets, newWidget] }
                    : s
            )
        );
        setSelectedWidgetId(newWidget.id);
        setShowCatalog(false);
    }, [activeSlideIndex, updateSlides]);

    const handleAddReportTemplate = useCallback((template: MasterReportTemplate) => {
        // Detect pages in report template: either metadata.reportPages or unique pageId across widgets
        const metaPages: { id: string; name: string }[] = (template as any).metadata?.reportPages || [];
        const widgetPages = Array.from(new Set(template.widgets.map((w: any) => w.pageId || 'page-1')));

        let resolvedPages: { id: string; name: string }[] = [];
        if (metaPages.length > 0) {
            resolvedPages = metaPages;
        } else if (widgetPages.length > 1) {
            resolvedPages = widgetPages.map((pid, idx) => ({ id: pid, name: `Pestaña ${idx + 1}` }));
        } else {
            resolvedPages = [{ id: 'page-1', name: template.name }];
        }

        if (resolvedPages.length <= 1) {
            // Single page: ALWAYS insert into currently active slide, preserving existing widgets (like titles)
            const targetPage = resolvedPages[0];
            const pageWidgets = template.widgets.filter((w: any) => !w.pageId || w.pageId === targetPage.id || widgetPages.length <= 1);
            
            const currentSlide = activeTemplate.slides[activeSlideIndex];
            const hasExistingWidgets = currentSlide && currentSlide.widgets.length > 0;
            const defaultY = hasExistingWidgets ? 18 : 3;
            const defaultHeight = hasExistingWidgets ? 78 : 94;

            const reportWidget: PresentationWidget = {
                id: genId(),
                type: 'report_template',
                x: 2,
                y: defaultY,
                width: 96,
                height: defaultHeight,
                label: template.name,
                config: {
                    templateId: template.id,
                    templateName: template.name,
                    pageId: targetPage.id,
                    pageName: targetPage.name,
                    themeId: template.themeId || 'corp-ld',
                    widgets: pageWidgets,
                }
            };

            updateSlides(slides =>
                slides.map((s, i) =>
                    i === activeSlideIndex
                        ? { ...s, widgets: [...s.widgets, reportWidget] }
                        : s
                )
            );
            setSelectedWidgetId(reportWidget.id);
            setShowCatalog(false);
        } else {
            // Multi-page template: prompt user whether to place all on same slide or separate slides
            setMultiPagePrompt({ template, resolvedPages });
            setShowCatalog(false);
        }
    }, [activeSlideIndex, activeTemplate.slides, updateSlides]);

    // Handle inserting all pages of multi-page template into current slide
    const handleInsertMultiPageSameSlide = useCallback((template: MasterReportTemplate, pages: { id: string; name: string }[]) => {
        const currentSlide = activeTemplate.slides[activeSlideIndex];
        const hasExisting = currentSlide && currentSlide.widgets.length > 0;
        const startY = hasExisting ? 18 : 4;
        const availH = hasExisting ? 78 : 92;

        const newWidgets: PresentationWidget[] = pages.map((page, idx) => {
            const pageWidgets = template.widgets.filter((w: any) => (w.pageId || 'page-1') === page.id);
            // If 2 pages, place them side-by-side (47% width each)
            const width = pages.length === 2 ? 47 : 96;
            const x = pages.length === 2 ? (idx === 0 ? 2 : 51) : 2;
            const height = pages.length === 2 ? availH : Math.max(35, Math.floor(availH / pages.length));
            const y = pages.length === 2 ? startY : startY + (idx * (height + 2));

            return {
                id: genId(),
                type: 'report_template',
                x,
                y,
                width,
                height,
                label: `${template.name} - ${page.name}`,
                config: {
                    templateId: template.id,
                    templateName: template.name,
                    pageId: page.id,
                    pageName: page.name,
                    pageIndex: idx,
                    totalPages: pages.length,
                    themeId: template.themeId || 'corp-ld',
                    widgets: pageWidgets,
                }
            };
        });

        updateSlides(slides =>
            slides.map((s, i) =>
                i === activeSlideIndex
                    ? { ...s, widgets: [...s.widgets, ...newWidgets] }
                    : s
            )
        );
        setMultiPagePrompt(null);
    }, [activeSlideIndex, activeTemplate.slides, updateSlides]);

    // Handle inserting multi-page template into separate slides
    const handleInsertMultiPageSeparateSlides = useCallback((template: MasterReportTemplate, pages: { id: string; name: string }[]) => {
        const currentSlide = activeTemplate.slides[activeSlideIndex];
        const hasExisting = currentSlide && currentSlide.widgets.length > 0;

        // First page goes into current slide
        const firstPage = pages[0];
        const firstWidgets = template.widgets.filter((w: any) => (w.pageId || 'page-1') === firstPage.id);
        const firstReportWidget: PresentationWidget = {
            id: genId(),
            type: 'report_template',
            x: 2,
            y: hasExisting ? 18 : 3,
            width: 96,
            height: hasExisting ? 78 : 94,
            label: `${template.name} - ${firstPage.name}`,
            config: {
                templateId: template.id,
                templateName: template.name,
                pageId: firstPage.id,
                pageName: firstPage.name,
                pageIndex: 0,
                totalPages: pages.length,
                themeId: template.themeId || 'corp-ld',
                widgets: firstWidgets,
            }
        };

        // Remaining pages go into newly generated consecutive slides
        const remainingPages = pages.slice(1);
        const newSlides: PresentationSlide[] = remainingPages.map((page, idx) => {
            const pageWidgets = template.widgets.filter((w: any) => (w.pageId || 'page-1') === page.id);
            const reportWidget: PresentationWidget = {
                id: genId(),
                type: 'report_template',
                x: 2,
                y: 3,
                width: 96,
                height: 94,
                label: `${template.name} - ${page.name}`,
                config: {
                    templateId: template.id,
                    templateName: template.name,
                    pageId: page.id,
                    pageName: page.name,
                    pageIndex: idx + 1,
                    totalPages: pages.length,
                    themeId: template.themeId || 'corp-ld',
                    widgets: pageWidgets,
                }
            };
            return {
                id: genId(),
                order: 0,
                background: currentSlide?.background || '#ffffff',
                widgets: [reportWidget]
            };
        });

        updateSlides(slides => {
            const before = slides.slice(0, activeSlideIndex);
            const currentUpdated: PresentationSlide = {
                ...slides[activeSlideIndex],
                widgets: [...slides[activeSlideIndex].widgets, firstReportWidget]
            };
            const after = slides.slice(activeSlideIndex + 1);
            return [...before, currentUpdated, ...newSlides, ...after].map((s, i) => ({ ...s, order: i }));
        });
        setMultiPagePrompt(null);
    }, [activeSlideIndex, activeTemplate.slides, updateSlides]);

    const updateWidgetConfig = useCallback((widgetId: string, config: Record<string, any>) => {
        updateSlides(slides =>
            slides.map((s, i) =>
                i === activeSlideIndex
                    ? { ...s, widgets: s.widgets.map(w => w.id === widgetId ? { ...w, config: { ...w.config, ...config } } : w) }
                    : s
            )
        );
    }, [activeSlideIndex, updateSlides]);

    const applySlideTheme = useCallback((background: string, applyToAll = false) => {
        updateSlides(slides =>
            slides.map((s, i) =>
                applyToAll || i === activeSlideIndex
                    ? { ...s, background }
                    : s
            )
        );
    }, [activeSlideIndex, updateSlides]);

    const deleteWidget = useCallback((widgetId: string) => {
        updateSlides(slides =>
            slides.map((s, i) =>
                i === activeSlideIndex
                    ? { ...s, widgets: s.widgets.filter(w => w.id !== widgetId) }
                    : s
            )
        );
        if (selectedWidgetId === widgetId) setSelectedWidgetId(null);
    }, [activeSlideIndex, selectedWidgetId, updateSlides]);

    const updateWidgetPos = useCallback((widgetId: string, x: number, y: number) => {
        updateSlides(slides =>
            slides.map((s, i) =>
                i === activeSlideIndex
                    ? { ...s, widgets: s.widgets.map(w => w.id === widgetId ? { ...w, x, y } : w) }
                    : s
            )
        );
    }, [activeSlideIndex, updateSlides]);

    const updateWidgetSize = useCallback((widgetId: string, width: number, height: number, x: number, y: number) => {
        updateSlides(slides =>
            slides.map((s, i) =>
                i === activeSlideIndex
                    ? { ...s, widgets: s.widgets.map(w => w.id === widgetId ? { ...w, width, height, x, y } : w) }
                    : s
            )
        );
    }, [activeSlideIndex, updateSlides]);

    // --- Persistence ---
    const handleSave = useCallback(async () => {
        setIsSaving(true);
        setSaveStatus('idle');
        try {
            if (isMasterMode) {
                const saved = await saveMasterTemplate(activeTemplate);
                setActiveTemplate(saved);
                setSaveStatus('saved');
                if (onSaveMaster) onSaveMaster(saved);
                fetchMasterTemplates().then(setTemplates).catch(console.error);
                setTimeout(() => setSaveStatus('idle'), 2000);
            } else {
                const result = await saveTemplate(activeTemplate);
                if (result) {
                    setActiveTemplate(result);
                    setSaveStatus('saved');
                    // Refresh template list immediately
                    if (projectInfo.id) {
                        try {
                            const refreshed = await fetchTemplates(projectInfo.id);
                            setTemplates(refreshed);
                        } catch (e) {
                            console.error('Error refreshing templates:', e);
                        }
                    }
                    setTimeout(() => setSaveStatus('idle'), 2000);
                } else {
                    setSaveStatus('error');
                }
            }
        } catch (err) {
            console.error('Error saving template:', err);
            setSaveStatus('error');
        } finally {
            setIsSaving(false);
        }
    }, [activeTemplate, isMasterMode, onSaveMaster, projectInfo.id]);

    const handleLoadTemplate = useCallback((tmpl: PresentationTemplate) => {
        if (!isMasterMode && tmpl.projectId === 'global') {
            // Importar plantilla maestra corporativa al proyecto actual
            // Generar UUID válido para compatibilidad total con Postgres/Supabase
            const newId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : '';
            const importedTemplate: PresentationTemplate = {
                ...JSON.parse(JSON.stringify(tmpl)),
                id: newId,
                projectId: projectInfo?.id || '',
                name: `${tmpl.name}`,
                createdBy: currentUserName,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };
            setActiveTemplate(importedTemplate);
        } else {
            setActiveTemplate(tmpl);
        }
        setActiveSlideIndex(0);
        setSelectedWidgetId(null);
        setShowTemplateList(false);
    }, [isMasterMode, projectInfo?.id, currentUserName]);

    const handleDeleteTemplate = useCallback(async (id: string) => {
        if (!confirm('¿Eliminar esta plantilla permanentemente?')) return;
        const ok = isMasterMode ? await deleteMasterTemplate(id) : await deleteTemplate(id, projectInfo?.id);
        if (ok) {
            setTemplates(prev => prev.filter(t => t.id !== id));
            if (activeTemplate.id === id) {
                setActiveTemplate(createDefaultTemplate(isMasterMode ? 'global' : (projectInfo.id || ''), currentUserName));
            }
        }
    }, [activeTemplate.id, isMasterMode, projectInfo.id, currentUserName]);

    const handleNewTemplate = useCallback(() => {
        setActiveTemplate(createDefaultTemplate(isMasterMode ? 'global' : (projectInfo.id || ''), currentUserName));
        setActiveSlideIndex(0);
        setSelectedWidgetId(null);
        setShowTemplateList(false);
    }, [isMasterMode, projectInfo.id, currentUserName]);

    // --- Canvas pixel<->% conversion ---
    const getCanvasRect = useCallback(() => {
        if (!canvasRef.current) return { width: 960, height: 540 };
        const rect = canvasRef.current.getBoundingClientRect();
        return { width: rect.width, height: rect.height };
    }, []);

    // --- Keyboard shortcuts ---
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Delete' && selectedWidgetId) {
                deleteWidget(selectedWidgetId);
            }
            if (e.key === 'Escape') {
                setSelectedWidgetId(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedWidgetId, deleteWidget]);

    // --- Name editing ---
    const startEditName = () => {
        setEditNameValue(activeTemplate.name);
        setIsEditingName(true);
    };
    const confirmEditName = () => {
        setActiveTemplate(prev => ({ ...prev, name: editNameValue.trim() || 'Sin nombre' }));
        setIsEditingName(false);
    };

    // ====================== RENDER ======================
    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: '#0f172a',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: "'Inter', 'Segoe UI', sans-serif",
        }}>
            {/* ===== TOP BAR ===== */}
            <div style={{
                height: '52px',
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                borderBottom: '1px solid #334155',
                display: 'flex',
                alignItems: 'center',
                padding: '0 16px',
                gap: '12px',
                flexShrink: 0,
            }}>
                {/* Close */}
                <button
                    onClick={onClose}
                    style={{
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        padding: '6px 10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: '#94a3b8',
                        fontSize: '12px',
                        fontWeight: 500,
                        transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#94a3b8'; }}
                >
                    <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>arrow_back</span>
                    Volver
                </button>

                {/* Divider */}
                <div style={{ width: '1px', height: '24px', background: '#334155' }} />

                {/* Template name */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#60a5fa' }}>slideshow</span>
                    {isEditingName ? (
                        <input
                            autoFocus
                            value={editNameValue}
                            onChange={e => setEditNameValue(e.target.value)}
                            onBlur={confirmEditName}
                            onKeyDown={e => { if (e.key === 'Enter') confirmEditName(); if (e.key === 'Escape') setIsEditingName(false); }}
                            style={{
                                background: 'rgba(255,255,255,0.1)',
                                border: '1px solid #3b82f6',
                                borderRadius: '6px',
                                padding: '4px 10px',
                                color: '#fff',
                                fontSize: '14px',
                                fontWeight: 600,
                                outline: 'none',
                                width: '300px',
                            }}
                        />
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span
                                onClick={startEditName}
                                style={{
                                    color: '#e2e8f0',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    transition: 'background 0.15s',
                                }}
                                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                            >
                                {activeTemplate.name}
                            </span>
                            {isMasterMode && (
                                <span style={{
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    color: '#a78bfa',
                                    background: 'rgba(139,92,246,0.18)',
                                    border: '1px solid rgba(139,92,246,0.4)',
                                    padding: '2px 8px',
                                    borderRadius: '6px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                }}>
                                    Plantilla Maestra Corporativa
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Slide indicator */}
                <span style={{ color: '#64748b', fontSize: '12px', fontWeight: 500 }}>
                    Diapositiva {activeSlideIndex + 1} / {activeTemplate.slides.length}
                </span>

                {/* Actions */}
                <button
                    onClick={() => setShowTemplateList(!showTemplateList)}
                    style={{
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        padding: '6px 12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: '#94a3b8',
                        fontSize: '12px',
                        fontWeight: 500,
                        transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#94a3b8'; }}
                >
                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>folder_open</span>
                    Mis Plantillas
                </button>

                <button
                    onClick={() => setShowThemePicker(true)}
                    style={{
                        background: 'rgba(217,119,6,0.15)',
                        border: '1px solid rgba(217,119,6,0.3)',
                        borderRadius: '8px',
                        padding: '6px 12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: '#fbbf24',
                        fontSize: '12px',
                        fontWeight: 600,
                        transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(217,119,6,0.25)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(217,119,6,0.15)'; }}
                    title="Temas, colores y estilos de diapositiva estilo PowerPoint"
                >
                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>palette</span>
                    Diseño Diapositiva
                </button>

                <button
                    onClick={() => setShowCatalog(!showCatalog)}
                    style={{
                        background: showCatalog ? '#3b82f6' : 'rgba(59,130,246,0.15)',
                        border: `1px solid ${showCatalog ? '#3b82f6' : 'rgba(59,130,246,0.3)'}`,
                        borderRadius: '8px',
                        padding: '6px 12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: showCatalog ? '#fff' : '#60a5fa',
                        fontSize: '12px',
                        fontWeight: 600,
                        transition: 'all 0.15s',
                    }}
                >
                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>add_circle</span>
                    Añadir Widget
                </button>

                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    style={{
                        background: saveStatus === 'saved' ? '#059669' : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '6px 16px',
                        cursor: isSaving ? 'wait' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 700,
                        transition: 'all 0.2s',
                        opacity: isSaving ? 0.7 : 1,
                        boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
                    }}
                >
                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                        {saveStatus === 'saved' ? 'check' : isSaving ? 'sync' : 'save'}
                    </span>
                    {saveStatus === 'saved' ? 'Guardado' : isSaving ? 'Guardando...' : 'Guardar'}
                </button>
            </div>

            {/* ===== MAIN AREA ===== */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

                {/* ===== LEFT: SLIDE PANEL ===== */}
                <div style={{
                    width: '160px',
                    background: '#1e293b',
                    borderRight: '1px solid #334155',
                    display: 'flex',
                    flexDirection: 'column',
                    flexShrink: 0,
                }}>
                    <div style={{
                        padding: '12px',
                        borderBottom: '1px solid #334155',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Diapositivas
                        </span>
                        <button
                            onClick={addSlide}
                            style={{
                                background: '#3b82f6',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '4px 8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '3px',
                                color: '#fff',
                                fontSize: '10px',
                                fontWeight: 600,
                            }}
                        >
                            <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>add</span>
                        </button>
                    </div>

                    {/* Slide thumbnails */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
                        {activeTemplate.slides.map((slide, index) => (
                            <div
                                key={slide.id}
                                onClick={() => { setActiveSlideIndex(index); setSelectedWidgetId(null); }}
                                style={{
                                    marginBottom: '8px',
                                    borderRadius: '8px',
                                    border: index === activeSlideIndex ? '2px solid #3b82f6' : '2px solid transparent',
                                    background: index === activeSlideIndex ? 'rgba(59,130,246,0.1)' : 'rgba(255,255,255,0.03)',
                                    cursor: 'pointer',
                                    overflow: 'hidden',
                                    transition: 'all 0.15s',
                                }}
                            >
                                {/* Mini 16:9 preview */}
                                <div style={{
                                    aspectRatio: '16/9',
                                    background: slide.background || '#ffffff',
                                    borderRadius: '6px',
                                    margin: '4px',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}>
                                    {/* Mini widget boxes */}
                                    {slide.widgets.map(w => (
                                        <div
                                            key={w.id}
                                            style={{
                                                position: 'absolute',
                                                left: `${w.x}%`,
                                                top: `${w.y}%`,
                                                width: `${w.width}%`,
                                                height: `${w.height}%`,
                                                background: '#e2e8f0',
                                                borderRadius: '2px',
                                                border: '0.5px solid #cbd5e1',
                                            }}
                                        />
                                    ))}
                                </div>
                                {/* Slide number and actions */}
                                <div style={{
                                    padding: '4px 8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}>
                                    <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>
                                        {index + 1}
                                    </span>
                                    <div style={{ display: 'flex', gap: '2px' }}>
                                        <button
                                            onClick={e => { e.stopPropagation(); moveSlide(index, 'up'); }}
                                            disabled={index === 0}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', borderRadius: '4px', opacity: index === 0 ? 0.3 : 1 }}
                                        >
                                            <span className="material-symbols-outlined" style={{ fontSize: '12px', color: '#64748b' }}>arrow_upward</span>
                                        </button>
                                        <button
                                            onClick={e => { e.stopPropagation(); moveSlide(index, 'down'); }}
                                            disabled={index === activeTemplate.slides.length - 1}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', borderRadius: '4px', opacity: index === activeTemplate.slides.length - 1 ? 0.3 : 1 }}
                                        >
                                            <span className="material-symbols-outlined" style={{ fontSize: '12px', color: '#64748b' }}>arrow_downward</span>
                                        </button>
                                        <button
                                            onClick={e => { e.stopPropagation(); duplicateSlide(index); }}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', borderRadius: '4px' }}
                                        >
                                            <span className="material-symbols-outlined" style={{ fontSize: '12px', color: '#64748b' }}>content_copy</span>
                                        </button>
                                        <button
                                            onClick={e => { e.stopPropagation(); deleteSlide(index); }}
                                            disabled={activeTemplate.slides.length <= 1}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', borderRadius: '4px', opacity: activeTemplate.slides.length <= 1 ? 0.3 : 1 }}
                                        >
                                            <span className="material-symbols-outlined" style={{ fontSize: '12px', color: '#ef4444' }}>delete</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ===== CENTER: CANVAS ===== */}
                <div
                    style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#0f172a',
                        padding: '24px',
                        overflow: 'hidden',
                        position: 'relative',
                    }}
                    onClick={() => setSelectedWidgetId(null)}
                >
                    {/* Template list overlay */}
                    {showTemplateList && (
                        <>
                            <div
                                style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100 }}
                                onClick={() => setShowTemplateList(false)}
                            />
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '480px',
                                maxHeight: '70vh',
                                background: '#1e293b',
                                borderRadius: '16px',
                                border: '1px solid #334155',
                                boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
                                zIndex: 101,
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                            }}>
                                <div style={{ padding: '16px 20px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: '15px' }}>Mis Plantillas</span>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button
                                            onClick={handleNewTemplate}
                                            style={{
                                                background: '#3b82f6', border: 'none', borderRadius: '8px', padding: '6px 12px',
                                                color: '#fff', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                                                display: 'flex', alignItems: 'center', gap: '4px',
                                            }}
                                        >
                                            <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>add</span> Nueva
                                        </button>
                                        <button onClick={() => setShowTemplateList(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                                            <span className="material-symbols-outlined" style={{ fontSize: '18px', color: '#94a3b8' }}>close</span>
                                        </button>
                                    </div>
                                </div>
                                <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
                                    {templates.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
                                            <span className="material-symbols-outlined" style={{ fontSize: '40px', color: '#475569' }}>folder_off</span>
                                            <div style={{ fontSize: '13px', marginTop: '8px' }}>No hay plantillas guardadas</div>
                                        </div>
                                    ) : (
                                        templates.map(tmpl => {
                                            const isCorporateMaster = tmpl.projectId === 'global';
                                            return (
                                                <div
                                                    key={tmpl.id}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '12px',
                                                        padding: '10px 12px',
                                                        borderRadius: '10px',
                                                        marginBottom: '6px',
                                                        background: tmpl.id === activeTemplate.id ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.03)',
                                                        border: tmpl.id === activeTemplate.id ? '1px solid rgba(59,130,246,0.3)' : '1px solid transparent',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.15s',
                                                    }}
                                                    onClick={() => handleLoadTemplate(tmpl)}
                                                    onMouseEnter={e => { if (tmpl.id !== activeTemplate.id) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                                                    onMouseLeave={e => { if (tmpl.id !== activeTemplate.id) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                                                >
                                                    <span className="material-symbols-outlined" style={{ fontSize: '22px', color: isCorporateMaster ? '#a78bfa' : '#60a5fa' }}>slideshow</span>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <span style={{ fontSize: '13px', fontWeight: 600, color: '#e2e8f0' }} className="truncate">{tmpl.name}</span>
                                                            {isCorporateMaster && (
                                                                <span style={{
                                                                    fontSize: '9px',
                                                                    fontWeight: 700,
                                                                    color: '#c4b5fd',
                                                                    background: 'rgba(139,92,246,0.2)',
                                                                    border: '1px solid rgba(139,92,246,0.4)',
                                                                    padding: '1px 6px',
                                                                    borderRadius: '4px',
                                                                    textTransform: 'uppercase',
                                                                    flexShrink: 0,
                                                                }}>
                                                                    Maestra
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>
                                                            {tmpl.slides.length} diap. · {isCorporateMaster && !isMasterMode ? 'Plantilla Corporativa' : tmpl.updatedAt ? new Date(tmpl.updatedAt).toLocaleDateString('es-CL') : '—'}
                                                        </div>
                                                    </div>
                                                    {(!isCorporateMaster || isMasterMode) && (
                                                        <button
                                                            onClick={e => { e.stopPropagation(); handleDeleteTemplate(tmpl.id); }}
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '6px' }}
                                                            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.1)')}
                                                            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                                                            title="Eliminar plantilla"
                                                        >
                                                            <span className="material-symbols-outlined" style={{ fontSize: '16px', color: '#ef4444' }}>delete</span>
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {/* The 16:9 Canvas */}
                    <div
                        ref={canvasRef}
                        onClick={e => e.stopPropagation()}
                        style={{
                            aspectRatio: '16 / 9',
                            width: '100%',
                            maxWidth: 'calc((100vh - 100px) * 16 / 9)',
                            maxHeight: 'calc(100vh - 100px)',
                            background: activeSlide.background || '#ffffff',
                            borderRadius: '8px',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Grid overlay for alignment help */}
                        <svg
                            viewBox="0 0 100 100"
                            preserveAspectRatio="none"
                            style={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                pointerEvents: 'none',
                                opacity: 0.08,
                            }}
                        >
                            {[10, 20, 30, 40, 50, 60, 70, 80, 90].map(v => (
                                <React.Fragment key={v}>
                                    <line x1={v} y1="0" x2={v} y2="100" stroke="#94a3b8" strokeWidth="0.15" />
                                    <line x1="0" y1={v} x2="100" y2={v} stroke="#94a3b8" strokeWidth="0.15" />
                                </React.Fragment>
                            ))}
                        </svg>

                        {/* Widgets */}
                        {activeSlide.widgets.map(widget => {
                            const canvas = getCanvasRect();
                            const pxX = (widget.x / 100) * canvas.width;
                            const pxY = (widget.y / 100) * canvas.height;
                            const pxW = (widget.width / 100) * canvas.width;
                            const pxH = (widget.height / 100) * canvas.height;
                            const isSelected = selectedWidgetId === widget.id;

                            return (
                                <Rnd
                                    key={widget.id}
                                    position={{ x: pxX, y: pxY }}
                                    size={{ width: pxW, height: pxH }}
                                    bounds="parent"
                                    minWidth={40}
                                    minHeight={30}
                                    onDragStop={(_e, d) => {
                                        const rect = getCanvasRect();
                                        updateWidgetPos(widget.id, (d.x / rect.width) * 100, (d.y / rect.height) * 100);
                                    }}
                                    onResizeStop={(_e, _dir, ref, _delta, position) => {
                                        const rect = getCanvasRect();
                                        const newW = (parseFloat(ref.style.width) / rect.width) * 100;
                                        const newH = (parseFloat(ref.style.height) / rect.height) * 100;
                                        const newX = (position.x / rect.width) * 100;
                                        const newY = (position.y / rect.height) * 100;
                                        updateWidgetSize(widget.id, newW, newH, newX, newY);
                                    }}
                                    onMouseDown={e => {
                                        e.stopPropagation();
                                        setSelectedWidgetId(widget.id);
                                    }}
                                    style={{
                                        outline: isSelected ? '2px solid #3b82f6' : '1px solid transparent',
                                        borderRadius: '8px',
                                        boxShadow: isSelected ? '0 0 0 4px rgba(59,130,246,0.15), 0 4px 12px rgba(0,0,0,0.1)' : '0 1px 4px rgba(0,0,0,0.06)',
                                        transition: 'outline 0.1s, box-shadow 0.1s',
                                        cursor: 'move',
                                        overflow: 'hidden',
                                    }}
                                    enableResizing={isSelected}
                                    resizeHandleStyles={{
                                        bottomRight: { width: '12px', height: '12px', right: '-6px', bottom: '-6px', cursor: 'nwse-resize', background: '#3b82f6', borderRadius: '50%' },
                                        bottomLeft: { width: '12px', height: '12px', left: '-6px', bottom: '-6px', cursor: 'nesw-resize', background: '#3b82f6', borderRadius: '50%' },
                                        topRight: { width: '12px', height: '12px', right: '-6px', top: '-6px', cursor: 'nesw-resize', background: '#3b82f6', borderRadius: '50%' },
                                        topLeft: { width: '12px', height: '12px', left: '-6px', top: '-6px', cursor: 'nwse-resize', background: '#3b82f6', borderRadius: '50%' },
                                    }}
                                >
                                    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                                        <WidgetRenderer
                                            widget={widget}
                                            isSelected={isSelected}
                                            weeklyPlanData={weeklyPlanData}
                                            onSplitWidgetIntoSlides={splitWidgetIntoSlides}
                                            projectId={isMasterMode ? (weeklyPlanData?.projectInfo?.id || undefined) : (projectInfo?.id || undefined)}
                                            onUpdateConfig={updateWidgetConfig}
                                        />

                                        {/* Floating Formatting Toolbar for Text, Title, Shape and Image */}
                                        {isSelected && (widget.type === 'title_block' || widget.type === 'text_block' || widget.type === 'shape_block' || widget.type === 'image_block') && (
                                            <div
                                                onMouseDown={e => e.stopPropagation()}
                                                style={{
                                                    position: 'absolute',
                                                    top: '-42px',
                                                    left: '0',
                                                    background: '#1e293b',
                                                    border: '1px solid #475569',
                                                    borderRadius: '8px',
                                                    padding: '4px 8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    boxShadow: '0 10px 25px rgba(0,0,0,0.4)',
                                                    zIndex: 20,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {/* Text / Title controls */}
                                                {(widget.type === 'title_block' || widget.type === 'text_block') && (
                                                    <>
                                                        {/* Font Size */}
                                                        <select
                                                            value={widget.config?.fontSize || (widget.type === 'title_block' ? 26 : 13)}
                                                            onChange={e => updateWidgetConfig(widget.id, { fontSize: parseInt(e.target.value, 10) })}
                                                            style={{ background: '#334155', color: '#fff', border: 'none', borderRadius: '4px', padding: '2px 4px', fontSize: '11px', outline: 'none' }}
                                                            title="Tamaño de fuente"
                                                        >
                                                            {[10, 11, 12, 13, 14, 16, 18, 20, 24, 28, 32, 36, 42, 48].map(s => (
                                                                <option key={s} value={s}>{s}px</option>
                                                            ))}
                                                        </select>

                                                        {/* Bold */}
                                                        <button
                                                            type="button"
                                                            onClick={() => updateWidgetConfig(widget.id, { fontWeight: widget.config?.fontWeight === 'bold' || widget.config?.fontWeight === '800' ? 'normal' : 'bold' })}
                                                            style={{
                                                                background: widget.config?.fontWeight === 'bold' || widget.config?.fontWeight === '800' ? '#3b82f6' : '#334155',
                                                                color: '#fff', border: 'none', borderRadius: '4px', width: '24px', height: '24px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px'
                                                            }}
                                                            title="Negrita"
                                                        >
                                                            B
                                                        </button>

                                                        {/* Italic */}
                                                        <button
                                                            type="button"
                                                            onClick={() => updateWidgetConfig(widget.id, { fontStyle: widget.config?.fontStyle === 'italic' ? 'normal' : 'italic' })}
                                                            style={{
                                                                background: widget.config?.fontStyle === 'italic' ? '#3b82f6' : '#334155',
                                                                color: '#fff', border: 'none', borderRadius: '4px', width: '24px', height: '24px', cursor: 'pointer', fontStyle: 'italic', fontSize: '12px'
                                                            }}
                                                            title="Cursiva"
                                                        >
                                                            I
                                                        </button>

                                                        {/* Align Left */}
                                                        <button
                                                            type="button"
                                                            onClick={() => updateWidgetConfig(widget.id, { textAlign: 'left' })}
                                                            style={{
                                                                background: (widget.config?.textAlign || 'left') === 'left' ? '#3b82f6' : '#334155',
                                                                color: '#fff', border: 'none', borderRadius: '4px', width: '24px', height: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                            }}
                                                            title="Alinear a la izquierda"
                                                        >
                                                            <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>format_align_left</span>
                                                        </button>

                                                        {/* Align Center */}
                                                        <button
                                                            type="button"
                                                            onClick={() => updateWidgetConfig(widget.id, { textAlign: 'center' })}
                                                            style={{
                                                                background: widget.config?.textAlign === 'center' ? '#3b82f6' : '#334155',
                                                                color: '#fff', border: 'none', borderRadius: '4px', width: '24px', height: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                            }}
                                                            title="Centrar"
                                                        >
                                                            <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>format_align_center</span>
                                                        </button>

                                                        {/* Color Picker */}
                                                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer' }} title="Color del texto">
                                                            <span className="material-symbols-outlined" style={{ fontSize: '15px', color: widget.config?.color || '#38bdf8' }}>format_color_text</span>
                                                            <input
                                                                type="color"
                                                                value={widget.config?.color || (widget.type === 'title_block' ? '#0f172a' : '#334155')}
                                                                onChange={e => updateWidgetConfig(widget.id, { color: e.target.value })}
                                                                style={{ width: '18px', height: '18px', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                                                            />
                                                        </label>

                                                        {/* Divider Line Toggle for Titles */}
                                                        {widget.type === 'title_block' && (
                                                            <button
                                                                type="button"
                                                                onClick={() => updateWidgetConfig(widget.id, { showDivider: !widget.config?.showDivider })}
                                                                style={{
                                                                    background: widget.config?.showDivider ? '#3b82f6' : '#334155',
                                                                    color: '#fff', border: 'none', borderRadius: '4px', padding: '2px 6px', fontSize: '10px', cursor: 'pointer', fontWeight: 600
                                                                }}
                                                                title="Línea inferior decorativa"
                                                            >
                                                                Línea
                                                            </button>
                                                        )}
                                                    </>
                                                )}

                                                {/* Shape Controls */}
                                                {widget.type === 'shape_block' && (
                                                    <>
                                                        <select
                                                            value={widget.config?.shapeType || 'rectangle'}
                                                            onChange={e => updateWidgetConfig(widget.id, { shapeType: e.target.value })}
                                                            style={{ background: '#334155', color: '#fff', border: 'none', borderRadius: '4px', padding: '2px 4px', fontSize: '11px', outline: 'none' }}
                                                            title="Tipo de forma"
                                                        >
                                                            <option value="rectangle">Rectángulo</option>
                                                            <option value="pill">Píldora / Cápsula</option>
                                                            <option value="circle">Círculo</option>
                                                            <option value="line">Línea Divisoria</option>
                                                        </select>

                                                        <label style={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer' }} title="Color de relleno">
                                                            <span style={{ fontSize: '10px', color: '#94a3b8' }}>Fondo:</span>
                                                            <input
                                                                type="color"
                                                                value={widget.config?.fillColor || '#2563eb'}
                                                                onChange={e => updateWidgetConfig(widget.id, { fillColor: e.target.value })}
                                                                style={{ width: '18px', height: '18px', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                                                            />
                                                        </label>

                                                        <button
                                                            type="button"
                                                            onClick={() => updateWidgetConfig(widget.id, { shadow: !widget.config?.shadow })}
                                                            style={{
                                                                background: widget.config?.shadow ? '#3b82f6' : '#334155',
                                                                color: '#fff', border: 'none', borderRadius: '4px', padding: '2px 6px', fontSize: '10px', cursor: 'pointer'
                                                            }}
                                                            title="Sombra suave"
                                                        >
                                                            Sombra
                                                        </button>
                                                    </>
                                                )}

                                                {/* Image Controls */}
                                                {widget.type === 'image_block' && (
                                                    <>
                                                        <select
                                                            value={widget.config?.objectFit || 'contain'}
                                                            onChange={e => updateWidgetConfig(widget.id, { objectFit: e.target.value })}
                                                            style={{ background: '#334155', color: '#fff', border: 'none', borderRadius: '4px', padding: '2px 4px', fontSize: '11px', outline: 'none' }}
                                                            title="Ajuste de imagen"
                                                        >
                                                            <option value="contain">Ajustar (Contener)</option>
                                                            <option value="cover">Llenar (Recortar)</option>
                                                            <option value="fill">Estirar</option>
                                                        </select>

                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const cap = prompt('Escriba un pie de foto / descripción:', widget.config?.caption || '');
                                                                if (cap !== null) updateWidgetConfig(widget.id, { caption: cap });
                                                            }}
                                                            style={{ background: '#334155', color: '#fff', border: 'none', borderRadius: '4px', padding: '2px 6px', fontSize: '10px', cursor: 'pointer' }}
                                                            title="Pie de foto"
                                                        >
                                                            Pie de foto
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {/* Delete button on selected widget */}
                                        {isSelected && (
                                            <button
                                                onClick={e => { e.stopPropagation(); deleteWidget(widget.id); }}
                                                style={{
                                                    position: 'absolute',
                                                    top: '-10px',
                                                    right: '-10px',
                                                    width: '22px',
                                                    height: '22px',
                                                    borderRadius: '50%',
                                                    background: '#ef4444',
                                                    border: '2px solid #fff',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                                                    zIndex: 25,
                                                }}
                                            >
                                                <span className="material-symbols-outlined" style={{ fontSize: '13px', color: '#fff' }}>close</span>
                                            </button>
                                        )}
                                    </div>
                                </Rnd>
                            );
                        })}

                        {/* Empty state */}
                        {activeSlide.widgets.length === 0 && (
                            <div style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#94a3b8',
                                pointerEvents: 'none',
                            }}>
                                <span className="material-symbols-outlined" style={{ fontSize: '48px', color: '#cbd5e1', marginBottom: '12px' }}>widgets</span>
                                <div style={{ fontSize: '16px', fontWeight: 600, color: '#64748b' }}>Diapositiva vacía</div>
                                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                                    Usa el botón "Añadir Widget" para agregar contenido
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ===== RIGHT: WIDGET CATALOG ===== */}
                {showCatalog && (
                    <WidgetCatalog
                        onAddWidget={addWidget}
                        onAddReportTemplate={handleAddReportTemplate}
                        onClose={() => setShowCatalog(false)}
                    />
                )}
            </div>

            {/* ===== THEME & SLIDE DESIGN PICKER MODAL ===== */}
            {showThemePicker && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(15, 23, 42, 0.75)',
                        backdropFilter: 'blur(6px)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '24px',
                    }}
                    onClick={() => setShowThemePicker(false)}
                >
                    <div
                        style={{
                            background: '#1e293b',
                            borderRadius: '16px',
                            border: '1px solid #334155',
                            width: '100%',
                            maxWidth: '780px',
                            maxHeight: '90vh',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                            overflow: 'hidden',
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div style={{
                            padding: '18px 24px',
                            borderBottom: '1px solid #334155',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: '#0f172a'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '8px',
                                    background: 'linear-gradient(135deg, #d97706, #b45309)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#fff',
                                }}>
                                    <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>palette</span>
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#f8fafc' }}>
                                        Diseños y Estilos de Diapositiva
                                    </h3>
                                    <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>
                                        Aplica temas coordinados o personaliza el fondo al estilo de presentaciones ejecutivas
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowThemePicker(false)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#94a3b8',
                                    cursor: 'pointer',
                                    padding: '6px',
                                    borderRadius: '6px',
                                    display: 'flex',
                                }}
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        {/* Modal Body: Themes Grid */}
                        <div style={{ padding: '24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <div>
                                <div style={{ fontSize: '12px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>
                                    Temas Profesionales Predefinidos
                                </div>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                                    gap: '14px',
                                }}>
                                    {SLIDE_THEMES.map(theme => {
                                        const isCurrent = activeSlide?.background === theme.bg;
                                        return (
                                            <div
                                                key={theme.id}
                                                onClick={() => applySlideTheme(theme.bg, false)}
                                                style={{
                                                    background: '#0f172a',
                                                    border: `2px solid ${isCurrent ? '#3b82f6' : '#334155'}`,
                                                    borderRadius: '12px',
                                                    padding: '10px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.15s ease',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '10px',
                                                }}
                                                onMouseEnter={e => {
                                                    if (!isCurrent) e.currentTarget.style.borderColor = '#64748b';
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                }}
                                                onMouseLeave={e => {
                                                    if (!isCurrent) e.currentTarget.style.borderColor = '#334155';
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                }}
                                            >
                                                {/* Mini preview 16:9 */}
                                                <div style={{
                                                    width: '100%',
                                                    aspectRatio: '16/9',
                                                    borderRadius: '8px',
                                                    background: theme.bg,
                                                    border: '1px solid rgba(255,255,255,0.1)',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    padding: '8px',
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                }}>
                                                    {/* Mock slide content */}
                                                    <div style={{
                                                        width: '55%',
                                                        height: '6px',
                                                        borderRadius: '3px',
                                                        background: theme.isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)',
                                                        marginBottom: '4px'
                                                    }} />
                                                    <div style={{
                                                        width: '35%',
                                                        height: '3px',
                                                        borderRadius: '2px',
                                                        background: theme.isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)',
                                                        marginBottom: '6px'
                                                    }} />
                                                    <div style={{
                                                        flex: 1,
                                                        borderRadius: '4px',
                                                        border: `1px dashed ${theme.isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}`,
                                                        background: theme.isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                                                    }} />
                                                    {isCurrent && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '6px',
                                                            right: '6px',
                                                            background: '#2563eb',
                                                            color: '#fff',
                                                            borderRadius: '50%',
                                                            width: '18px',
                                                            height: '18px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '12px'
                                                        }}>
                                                            ✓
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#f8fafc' }}>
                                                        {theme.name}
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px', lineHeight: 1.3 }}>
                                                        {theme.desc}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Custom color / gradient */}
                            <div style={{
                                background: '#0f172a',
                                border: '1px solid #334155',
                                borderRadius: '12px',
                                padding: '16px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px',
                            }}>
                                <div style={{ fontSize: '12px', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Color o Fondo Personalizado
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <span style={{ fontSize: '13px', color: '#94a3b8' }}>Color Sólido:</span>
                                        <input
                                            type="color"
                                            value={activeSlide?.background?.startsWith('#') ? activeSlide.background : '#ffffff'}
                                            onChange={e => applySlideTheme(e.target.value, false)}
                                            style={{
                                                width: '36px',
                                                height: '36px',
                                                border: '1px solid #475569',
                                                borderRadius: '6px',
                                                background: 'none',
                                                cursor: 'pointer',
                                                padding: 0
                                            }}
                                        />
                                    </label>

                                    <div style={{ flex: 1, minWidth: '220px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '13px', color: '#94a3b8' }}>CSS/Gradiente:</span>
                                        <input
                                            type="text"
                                            value={activeSlide?.background || ''}
                                            onChange={e => applySlideTheme(e.target.value, false)}
                                            placeholder="ej: linear-gradient(...) o #1e293b"
                                            style={{
                                                flex: 1,
                                                background: '#1e293b',
                                                border: '1px solid #475569',
                                                borderRadius: '6px',
                                                padding: '8px 12px',
                                                fontSize: '12px',
                                                color: '#fff',
                                                outline: 'none',
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div style={{
                            padding: '16px 24px',
                            borderTop: '1px solid #334155',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: '#0f172a'
                        }}>
                            <button
                                type="button"
                                onClick={() => {
                                    if (activeSlide?.background) {
                                        applySlideTheme(activeSlide.background, true);
                                        alert('¡Tema y fondo aplicados a todas las diapositivas de la presentación!');
                                    }
                                }}
                                style={{
                                    background: 'rgba(59, 130, 246, 0.15)',
                                    border: '1px solid rgba(59, 130, 246, 0.4)',
                                    color: '#60a5fa',
                                    borderRadius: '8px',
                                    padding: '8px 14px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                }}
                            >
                                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>auto_awesome</span>
                                Aplicar a todas las diapositivas
                            </button>

                            <button
                                type="button"
                                onClick={() => setShowThemePicker(false)}
                                style={{
                                    background: '#2563eb',
                                    border: 'none',
                                    color: '#fff',
                                    borderRadius: '8px',
                                    padding: '8px 18px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                Listo
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== MULTI-PAGE TEMPLATE INSERTION PROMPT MODAL ===== */}
            {multiPagePrompt && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(15, 23, 42, 0.75)',
                        backdropFilter: 'blur(6px)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '24px',
                    }}
                    onClick={() => setMultiPagePrompt(null)}
                >
                    <div
                        style={{
                            background: '#1e293b',
                            borderRadius: '16px',
                            border: '1px solid #334155',
                            width: '100%',
                            maxWidth: '560px',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6)',
                            overflow: 'hidden',
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div style={{
                            padding: '18px 24px',
                            borderBottom: '1px solid #334155',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: '#0f172a',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '8px',
                                    background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#fff',
                                }}>
                                    <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>auto_stories</span>
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#f8fafc' }}>
                                        Plantilla con Múltiples Páginas
                                    </h3>
                                    <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>
                                        "{multiPagePrompt.template.name}" contiene {multiPagePrompt.resolvedPages.length} páginas/pestañas
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setMultiPagePrompt(null)}
                                style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        {/* Content */}
                        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div style={{ fontSize: '13px', color: '#cbd5e1', lineHeight: 1.4 }}>
                                ¿Cómo deseas organizar las páginas de este reporte en tu presentación?
                            </div>

                            {/* Option 1: Misma Diapositiva */}
                            <div
                                onClick={() => handleInsertMultiPageSameSlide(multiPagePrompt.template, multiPagePrompt.resolvedPages)}
                                style={{
                                    border: '2px solid #334155',
                                    borderRadius: '12px',
                                    padding: '16px',
                                    cursor: 'pointer',
                                    background: '#0f172a',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '14px',
                                    transition: 'all 0.15s ease',
                                }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.borderColor = '#3b82f6';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.borderColor = '#334155';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                            >
                                <div style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '8px',
                                    background: 'rgba(59, 130, 246, 0.15)',
                                    color: '#60a5fa',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                    <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>dashboard_customize</span>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#f8fafc' }}>
                                        Todas en la diapositiva actual
                                    </div>
                                    <div style={{ fontSize: '11.5px', color: '#94a3b8', marginTop: '3px', lineHeight: 1.35 }}>
                                        Inserta las {multiPagePrompt.resolvedPages.length} páginas juntas en la diapositiva que estás trabajando para adaptar y combinar sus tamaños libremente.
                                    </div>
                                </div>
                            </div>

                            {/* Option 2: Diapositivas Separadas */}
                            <div
                                onClick={() => handleInsertMultiPageSeparateSlides(multiPagePrompt.template, multiPagePrompt.resolvedPages)}
                                style={{
                                    border: '2px solid #334155',
                                    borderRadius: '12px',
                                    padding: '16px',
                                    cursor: 'pointer',
                                    background: '#0f172a',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '14px',
                                    transition: 'all 0.15s ease',
                                }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.borderColor = '#3b82f6';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.borderColor = '#334155';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                            >
                                <div style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '8px',
                                    background: 'rgba(16, 185, 129, 0.15)',
                                    color: '#34d399',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                    <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>view_carousel</span>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#f8fafc' }}>
                                        Separar en distintas diapositivas
                                    </div>
                                    <div style={{ fontSize: '11.5px', color: '#94a3b8', marginTop: '3px', lineHeight: 1.35 }}>
                                        Crea una diapositiva independiente para cada página del reporte a pantalla completa.
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{
                            padding: '14px 24px',
                            borderTop: '1px solid #334155',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            background: '#0f172a',
                        }}>
                            <button
                                type="button"
                                onClick={() => setMultiPagePrompt(null)}
                                style={{
                                    background: '#334155',
                                    border: 'none',
                                    color: '#cbd5e1',
                                    borderRadius: '8px',
                                    padding: '7px 16px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PresentationEditor;
